import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";

import PanelTemplate from "../../templates/Panel";
import PageHeader from "../../components/PageHeader";
import Box from "../../components/Box";
import Table from "../../components/Table";
import Button from "../../components/Button";
import FilterSimple from "../../components/FilterSimple";
import Pagination from "../../components/Pagination";
import SortHeader from "../../components/SortHeader";

import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { hasPermission } from "../../services/auth";
import { convertIntToMoney } from "../../helpers/conversions";

import ModalPurchaseOrderForm from "./ModalPurchaseOrderForm";
import PurchaseOrderModals from "./PurchaseOrderModals";
import { StatusPill } from "./PurchaseOrderStatus";
import { fetchPurchaseOrderStatuses, getKindLabel } from "./helpers";

const searchParams = [
    { name: 'Fornecedor ou código', key: '[company_trade_name]', operation: 'contain', type: 'text' },
];

const indexMap = [
    { index: 'Código', value: 'code' },
    { index: 'Fornecedor', value: 'company_trade_name' },
    { index: 'Valor', value: 'total' },
    { index: 'Status', value: 'status_updated_at' },
    { index: 'Previsão', value: 'expected_delivery_date' },
];

const normalizePage = page => {
    const parsed = parseInt(page, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
};

const parseFilters = search => {
    const params = new URLSearchParams(search);

    return {
        page: normalizePage(params.get('page')),
        search: params.get('search') || '',
        status: params.get('status') || '',
        kind: params.get('kind') || '',
        // Recorte por pedido, vindo da aba financeira do pedido.
        orderId: params.get('order_id') || '',
        orderCode: params.get('order_code') || '',
        ordenationItem: params.get('sort') || 'created_at',
        orientation: params.get('orientation') || 'desc',
    };
};

const buildApiQuery = filters => {
    const params = new URLSearchParams();

    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.status) params.set('status_id', filters.status);
    if (filters.kind) params.set('kind', filters.kind);
    if (filters.orderId) params.set('order_id', filters.orderId);
    params.set('ordenation_item', filters.ordenationItem);
    params.set('orientation', filters.orientation);
    params.set('page', filters.page);

    return params.toString();
};

const buildListSearch = filters => {
    const params = new URLSearchParams();

    if (filters.page > 1) params.set('page', filters.page);
    if (filters.search.trim() !== '') params.set('search', filters.search.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.kind) params.set('kind', filters.kind);
    if (filters.orderId) params.set('order_id', filters.orderId);
    if (filters.orderCode) params.set('order_code', filters.orderCode);
    if (filters.ordenationItem !== 'created_at') params.set('sort', filters.ordenationItem);
    if (filters.orientation !== 'desc') params.set('orientation', filters.orientation);

    return params.toString();
};

/**
 * Pedidos de compra (BDM-887): o que a empresa compra dos fornecedores para
 * atender os pedidos de venda.
 */
export const PurchaseOrderList = () => {
    const [loading, setLoading] = useState(true);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [data, setData] = useState({ header: [], rows: [] });
    const [selected, setSelected] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [statuses, setStatuses] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalResults, setTotalResults] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const filters = useMemo(() => parseFilters(location.search), [location.search]);

    const canManage = hasPermission('manage_purchase_order');

    useEffect(() => {
        fetchPurchaseOrderStatuses().then(setStatuses).catch(renderError);
    }, []);

    useEffect(() => {
        getPurchaseOrders();
    }, [location.search]);

    useEffect(() => {
        generateData();
    }, [purchaseOrders]);

    const updateUrl = (changes, resetPage = true) => {
        const next = { ...filters, ...changes, page: resetPage ? 1 : (changes.page || filters.page) };
        const search = buildListSearch(next);

        navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
    };

    const getPurchaseOrders = () => {
        setPurchaseOrders([]);
        setLoading(true);

        api.get(`/purchase-order/paginate?${buildApiQuery(filters)}`).then(res => {
            setPurchaseOrders(res.data.resources.data);
            setCurrentPage(res.data.resources.current_page);
            setLastPage(res.data.resources.last_page);
            setPerPage(res.data.resources.per_page);
            setTotalResults(res.data.resources.total);
            setHasNextPage(res.data.resources.next_page_url !== null);
            setHasPrevPage(res.data.resources.prev_page_url !== null);
        }).catch(renderError).then(() => setLoading(false));
    };

    const generateData = () => {
        const header = ['Código', 'Fornecedor', 'Tipo', 'Pedido', 'Valor', 'Previsão', 'Status'];

        const rows = purchaseOrders.map(purchaseOrder => ({
            data: [
                <>
                    {`#${purchaseOrder.code}`}
                    {purchaseOrder.created_by_automation &&
                        <span className={`finance-automation-tag`} title={`Gerado por automação do financeiro`}>auto</span>
                    }
                </>,
                purchaseOrder.company_trade_name ?? '—',
                getKindLabel(purchaseOrder.kind),
                purchaseOrder.order ? `#${purchaseOrder.order.code}` : '—',
                convertIntToMoney(purchaseOrder.total, true),
                purchaseOrder.expected_delivery_date
                    ? moment(purchaseOrder.expected_delivery_date).format('DD/MM/YYYY')
                    : '—',
                <span className={`purchase-order-status-cell`}>
                    <StatusPill status={purchaseOrder.status} />
                    {purchaseOrder.status_updated_at &&
                        <span
                            className={`updated`}
                            title={`Atualizado em ${moment(purchaseOrder.status_updated_at).format('DD/MM/YYYY [às] HH:mm')}`}
                        >
                            {moment(purchaseOrder.status_updated_at).fromNow()}
                        </span>
                    }
                </span>,
            ],
            action: () => setSelected(purchaseOrder),
        }));

        setData({ header, rows });
    };

    // Compra nova pode não entrar no recorte aberto (status, pedido, página):
    // os filtros caem para ela aparecer.
    const showAllAfterCreate = () => {
        if (location.search === '') {
            getPurchaseOrders();
        } else {
            navigate(location.pathname);
        }
    };

    return (
        <>
            <PurchaseOrderModals
                purchaseOrder={selected}
                close={() => setSelected(null)}
                onChanged={getPurchaseOrders}
            />

            <ModalPurchaseOrderForm
                show={showForm}
                close={() => setShowForm(false)}
                purchaseOrder={null}
                onSaved={showAllAfterCreate}
            />

            <PanelTemplate id="purchase-order-list">
                <PageHeader
                    left={
                        <FilterSimple
                            params={searchParams}
                            filter={(value, metadata) => updateUrl({ search: metadata?.value ?? '' })}
                            externalValue={filters.search}
                            externalOption={searchParams[0].key}
                        />
                    }
                    right={canManage &&
                        <Button
                            type={`primary`}
                            size={`small`}
                            svg={`plus-white`}
                            text={`Novo pedido de compra`}
                            action={() => setShowForm(true)}
                        />
                    }
                />

                {statuses.length > 0 &&
                    <div className={`purchase-order-status-filter`} role={`group`} aria-label={`Filtrar por status`}>
                        <button
                            className={!filters.status ? 'active' : ''}
                            onClick={() => updateUrl({ status: '' })}
                        >
                            Todos
                        </button>
                        {statuses.map(status => (
                            <button
                                key={status.id}
                                className={filters.status === status.id ? 'active' : ''}
                                style={{ '--status-color': status.color }}
                                onClick={() => updateUrl({ status: filters.status === status.id ? '' : status.id })}
                            >
                                <span className={`dot`} />
                                {status.name}
                            </button>
                        ))}
                    </div>
                }

                {/* Recorte que veio da aba financeira do pedido — a lista
                    precisa dizer que está filtrada. */}
                {filters.orderId &&
                    <div className={`finance-scope-notice`}>
                        <span>
                            Mostrando apenas as compras do pedido
                            {filters.orderCode ? <strong> #{filters.orderCode}</strong> : null}.
                        </span>
                        <button onClick={() => updateUrl({ orderId: '', orderCode: '' })}>
                            Ver todas
                        </button>
                    </div>
                }

                <Box className={`no-padding with-pagination`}>
                    <Table
                        header={data.header}
                        rows={data.rows}
                        loading={loading}
                        sortOrdenationAction={index => (
                            <SortHeader
                                index={index}
                                ordenationItem={filters.ordenationItem}
                                orientation={filters.orientation}
                                onSort={(item, orientation) => updateUrl({ ordenationItem: item, orientation })}
                                indexMap={indexMap}
                            />
                        )}
                    />
                </Box>

                {!loading &&
                    <Pagination
                        currentPage={currentPage}
                        lastPage={lastPage}
                        perPage={perPage}
                        totalResults={totalResults}
                        hasNextPage={hasNextPage}
                        hasPrevPage={hasPrevPage}
                        setCurrentPage={page => updateUrl({ page }, false)}
                    />
                }
            </PanelTemplate>
        </>
    );
};

export default PurchaseOrderList;
